import { useNavigate } from "react-router-dom";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import { ExperienceListDto } from "@intelligems/ig-types/dtos/experiences-list";
import { TabEnumFe } from "@intelligems/ig-types/fe-only/experiences";
import { officialCategoryName } from "@intelligems/ig-utils/common";
import { sortByStatus } from "@intelligems/ig-utils/experience-status";
import { Stack } from "@mui/material";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { TestTypeIcon } from "@src/features/experiments/components/TestTypeIcon";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { TabEnumToUserFacingName } from "@src/utils/namingConventions";

import { SearchOption } from "./types";
import { goToLabel, tokenizedMatches } from "./utils";

export const useRouteOptions = (
  query?: string,
  limit?: number
): SearchOption[] => {
  const experimentOptions = useExperienceOptions({
    query,
    limit,
    category: "experiment",
  });
  const personalizationOptions = useExperienceOptions({
    query,
    limit,
    category: "personalization",
  });

  return [...experimentOptions, ...personalizationOptions];
};

export const useExperienceOptions = ({
  query,
  limit,
  category,
}: {
  query?: string;
  limit?: number;
  category: ExperienceCategory;
}): SearchOption[] => {
  const experienceList = useGetExperiencesList({
    category,
  });
  const navigate = useNavigate();

  if (!experienceList.isSuccess) {
    return [];
  }
  const groupLabel = `${officialCategoryName(category, true)}s`;

  const options = sortByStatus(experienceList.data.experiencesList);

  const toOption = (
    exp: ExperienceListDto,
    tab: TabEnumFe = TabEnumFe.MODS,
    optionlabel?: string
  ): SearchOption => ({
    group: groupLabel,
    render: () => {
      const label = goToLabel(optionlabel ? optionlabel : exp.name);
      return (
        <Stack direction={"row"} alignItems={"center"}>
          <TestTypeIcon fontSize={"20px"} testFlowType={exp.type} />
          <IgTypography marginLeft={2}>{label}</IgTypography>
        </Stack>
      );
    },
    select: () => {
      const root =
        category === "experiment"
          ? EXPERIMENT_ROUTES.Experiment
          : PERSONALIZATION_ROUTES.Personalization;

      const path = `${root}/${exp.id}?action=edit&tab=${tab}`;
      void navigate(path);
    },
  });

  let filtered = options;
  if (query) {
    const lower = query.toLowerCase();
    filtered = filtered.filter(
      (exp) =>
        tokenizedMatches(lower, exp.name.toLowerCase()) ||
        exp.id.toLowerCase().includes(lower)
    );
  }

  if (filtered.length === 1) {
    const exp = filtered[0];
    return Object.values(TabEnumFe)
      .map((tab) => {
        if (tab === TabEnumFe.ANALYTICS && !exp.startedAtTs) {
          return;
        }
        return toOption(
          exp,
          tab,
          `${exp.name} - ${TabEnumToUserFacingName[tab]}`
        );
      })
      .filter((o): o is SearchOption => !!o);
  }

  return filtered.slice(0, limit).map((exp) => toOption(exp));
};
